import React, { useCallback, useContext, useEffect, useState } from 'react';
import { CertificateDetails, CertificateType } from 'store/api/types';
import { getErrorMessage } from 'utils/getErrorMessage/getErrorMessage';

import { Loader, LocaleContext, NotificationsContext, NotificationTypes } from './../../../../../shared';
import { AddCertificateContainer } from './addCertificate/AddCertificateContainer';
import { CertificateItem } from './certificateItem/CertificateItem';
import { CertificatesGroupTable, CertificatesGroupTitle, CertificatesGroupWrapper } from './CertificatesGroup.styles';
import { CertificateGroupProps } from './CertificatesGroup.types';
import { CertificatesHeader } from './certificatesHeader/CertificatesHeader';

export const CertificatesGroup = ({
  accountId,
  applicationId,
  onFetchCertificates,
  certificateType,
}: CertificateGroupProps) => {
  const [certificates, setCertificates] = useState<CertificateDetails[]>([]);
  const localeContext = useContext(LocaleContext);
  const notificationsContext = useContext(NotificationsContext);

  const [isLoading, setLoading] = useState<boolean>(true);

  const handleSuccess = useCallback(
    (response: any) => {
      if (response && response.payload) {
        setCertificates(response.payload);
        setLoading(false);
      }
    },
    [setCertificates, setLoading],
  );

  const handleError = useCallback(
    (response: any) => {
      notificationsContext.showNotification({
        message: getErrorMessage(response, localeContext),
        time: 4000,
        type: NotificationTypes.ERROR,
      });
    },
    [notificationsContext, localeContext],
  );

  useEffect(() => {
    if (accountId && applicationId) {
      onFetchCertificates(accountId, applicationId, certificateType).then(handleSuccess, handleError);
    }
  }, [accountId, applicationId, certificateType, onFetchCertificates, handleSuccess, handleError]);

  const getTitle = () => {
    switch (certificateType) {
      case CertificateType.QSEALC:
        return localeContext.formatMessage({ id: 'INTEGRATION-DATA.SIGNATURE-CERTIFICATE' });
      case CertificateType.QWAC:
        return localeContext.formatMessage({ id: 'INTEGRATION-DATA.TLS-CERTIFICATE' });
      default:
        return '';
    }
  };

  const onSubmitCallback = () => {
    setLoading(true);
    if (accountId && applicationId) {
      onFetchCertificates(accountId, applicationId, certificateType).then(handleSuccess, handleError);
    }
  };

  return (
    <CertificatesGroupWrapper className={`certificates-group__${certificateType}-table`}>
      <CertificatesGroupTitle className="certificates-group__title">{getTitle()}:</CertificatesGroupTitle>
      <AddCertificateContainer
        accountId={accountId}
        applicationId={applicationId}
        certificateType={certificateType}
        onSubmitCallback={onSubmitCallback}
      />
      <CertificatesGroupTable key={certificates.length} className="certificates-group__table">
        <CertificatesHeader />
        {!isLoading ? (
          certificates.map((item, i) => <CertificateItem key={i} {...item} />)
        ) : (
          <Loader fullScreen={false} />
        )}
      </CertificatesGroupTable>
    </CertificatesGroupWrapper>
  );
};
